import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '../../../hooks/useIsMobile';
import axios from "axios";
import { Trash2, SquarePen } from "lucide-react";
import AgGridActionMenu from '../../../components/AgGridAction/AgGridAction';
import { AgGridReact } from 'ag-grid-react';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import TitleCard from '../../../components/TitleCard/TitleCard';
import Loader from '../../../components/Loader/Loader';
import ReusableModal from "../../../components/Modal/ReusableModal";
import OriginForm from "./OriginsForm";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import MobileCardList from "../../../components/MobileCardList/MobileCardList";
import ActionButton from "../../../components/ActionButton/ActionButton";

const OriginsList = () => {
    const navigate = useNavigate();
    const gridApiRef = useRef(null);

    // estado UI
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedOrigin, setSelectedOrigin] = useState(null);

    // estado del confirm modal
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        type: "confirm",
        title: "Eliminar origen",
        message: "¿Seguro que deseas eliminar este origen?",
        onConfirm: null,
    });

    // MOBILE DETECTION
    const isMobile = useIsMobile();

    const components = useMemo(
        () => ({
            actionMenu: AgGridActionMenu,
        }),
        []
    );

    // columnas
    const [colDefs] = useState([
        { field: "company_name", headerName: "Compañía" },
        { field: "municipal_name", headerName: "Municipio" },
        { field: "department_name", headerName: "Departamento" },
        { field: "actor_type", headerName: "Tipo de Actor" },
        { field: "certified", headerName: "Certificado" },
        {
            headerName: "Opciones",
            cellRenderer: "actionMenu",
            cellRendererParams: {
                onEdit: (params) => handleEditOrigin(params),
                onDelete: (params) => handleDeleteOrigin(params)
            },
            sortable: false,
            filter: false,
            width: 140,
            cellStyle: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            },
        }
    ]);

    // COLUMN DEFINITIONS
    const defaultColDef = useMemo(() => ({
        flex: 1,
        filter: false,
        floatingFilter: false,
        editable: false,
        cellStyle: { fontWeight: 'normal' },
    }), []);

    const fetchPage = useCallback(async (pageZeroBased, size) => {
        const token = localStorage.getItem('access_token');
        const url = `${process.env.REACT_APP_API_URL}/origins/get-all?page=${pageZeroBased}&size=${size}`;
        const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        return res.data;
    }, []);

    const createDataSource = useCallback(() => {
        return {
            getRows: async (params) => {
                setLoading(true); // 🔥 comienza carga
                try {
                    const page = params.startRow / (params.endRow - params.startRow);
                    const size = params.endRow - params.startRow;

                    const response = await fetchPage(page, size);
                    params.successCallback(response.data, response.total);
                } catch (error) {
                    console.error("Error cargando orígenes:", error);
                    params.failCallback();
                } finally {
                    setLoading(false); // ✅ termina carga
                }
            },
        };
    }, [fetchPage]);

    const onGridReady = useCallback((params) => {
        gridApiRef.current = params.api;

        if (typeof params.api.setDatasource === "function") {
            params.api.setDatasource(createDataSource());
        } else {
            params.api.setGridOption("datasource", createDataSource());
        }
    }, [createDataSource]);

    const onPaginationChanged = useCallback(() => {
        if (!gridApiRef.current) return;

        const api = gridApiRef.current;
        const newPageSize = api.paginationGetPageSize();

        setPageSize((prev) => {
            if (prev !== newPageSize) {
                return newPageSize;
            }
            return prev;
        });
    }, []);

    // acciones
    const handleClickNew = () => navigate('../masters/origins-create');

    function handleEditOrigin(params) {
        const data = params?.rowData || params;
        // console.log("Editar origen:", data);
        setSelectedOrigin(data);
        setShowEditModal(true);
    }

    function handleDeleteOrigin(params) {
        const data = params?.rowData || params;
        const id = data?._id;
        if (!id) return;

        setConfirmModal({
            isOpen: true,
            type: "confirm",
            title: "Eliminar origen",
            message: "¿Seguro que deseas eliminar este origen?",
            onConfirm: async () => {
                try {
                    const token = localStorage.getItem('access_token');
                    await axios.delete(`${process.env.REACT_APP_API_URL}/origins/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });

                    setConfirmModal({
                        isOpen: true,
                        type: "success",
                        title: "Origen eliminado",
                        message: "El origen fue eliminado correctamente.",
                        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
                    });

                    if (gridApiRef.current?.purgeInfiniteCache) {
                        setLoading(true); // 🔥 mostrar spinner
                        gridApiRef.current.purgeInfiniteCache();
                    } else if (gridApiRef.current?.setDatasource) {
                        setLoading(true);
                        gridApiRef.current.setDatasource(createDataSource());
                    }
                } catch (error) {
                    setConfirmModal({
                        isOpen: true,
                        type: "error",
                        title: "Error al eliminar",
                        message: "No se pudo eliminar el origen.",
                        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
                    });
                }
            },
        });
    };

    // MOBILE VERSION (cards con paginación al backend)
    const [mobileOrigins, setMobileOrigins] = useState([]);
    const [mobilePage, setMobilePage] = useState(0);
    const [mobileTotal, setMobileTotal] = useState(0);
    const [mobileLoading, setMobileLoading] = useState(false);

    const loadMobileData = useCallback(async () => {
        setMobileLoading(true);
        try {
            const token = localStorage.getItem("access_token");
            const url = `${process.env.REACT_APP_API_URL}/origins/get-all`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
                params: { page: mobilePage, size: pageSize },
            });

            setMobileOrigins(response.data?.data || []);
            setMobileTotal(response.data?.total || 0);
        } catch (err) {
            console.error(err);
        } finally {
            setMobileLoading(false);
        }
    }, [mobilePage, pageSize]);

    const handleMobilePageChange = useCallback((nextPage) => {
        if (nextPage === mobilePage) return;
        setMobileLoading(true);
        setMobilePage(nextPage);
    }, [mobilePage]);

    useEffect(() => {
        if (!isMobile) return;
        loadMobileData();
    }, [isMobile, loadMobileData]);

    const totalMobilePages = Math.ceil(mobileTotal / pageSize);

    // RENDER
    return (
        <>
            <TitleCard title="Orígenes" />

            {/**Boton crear */}
            <div className="flex justify-end my-1">
                <ActionButton
                    onClick={handleClickNew}
                />
            </div>

            {/* DESKTOP: AG GRID */}
            {!isMobile && (
                <div className="relative ag-theme-material my-4" style={{ height: 530, width: '100%' }}>
                    {loading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                            <Loader message="Cargando orígenes..." />
                        </div>
                    )}

                    <AgGridReact
                        rowModelType="infinite"
                        pagination={true}
                        paginationPageSize={pageSize}
                        paginationPageSizeSelector={[10, 25, 50]}
                        cacheBlockSize={pageSize}
                        maxBlocksInCache={5}
                        components={components}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        onGridReady={onGridReady}
                        onPaginationChanged={onPaginationChanged}
                    />
                </div>
            )}

            <MobileCardList
                isMobile={isMobile}
                loading={mobileLoading}
                loaderText="Cargando orígenes..."
                data={mobileOrigins}
                mobilePage={mobilePage}
                setMobilePage={handleMobilePageChange}
                totalPages={totalMobilePages}
                totalItems={mobileTotal}
                pageSize={pageSize}
                renderItem={(origin) => (
                    <div className="flex">
                        <div className="flex-1 grid gap-4 grid-cols-1 [@media(min-width:430px)]:grid-cols-2">
                            <h2 className="text-lg font-semibold col-span-full">
                                {origin.company_name}
                            </h2>

                            <p className="text-sm text-gray-600">{origin.municipal_name}</p>
                            <p className="text-sm text-gray-600">{origin.department_name}</p>
                            <p className="text-sm text-gray-600">DC{origin.department_code}</p>
                            <p className="text-sm text-gray-600">tipo actor: {origin.actor_type}</p>
                            <p className="text-sm text-gray-600">Certificado: {origin.certified}</p>

                        </div>

                        <div className="flex flex-col justify-center items-end space-y-3">
                            <button onClick={() => handleEditOrigin(origin)}>
                                <SquarePen size={18} color="#16A34A" strokeWidth={1.2} />
                            </button>

                            <button onClick={() => handleDeleteOrigin(origin)}>
                                <Trash2 size={18} color="#B91C1C" strokeWidth={1.2} />
                            </button>
                        </div>
                    </div>
                )}
            />

            {/* Modal de edición */}
            <ReusableModal
                isOpen={showEditModal}
                title="Editar Origen"
                onClose={() => setShowEditModal(false)}
            >
                <OriginForm
                    initialData={selectedOrigin}
                    onSuccess={() => {
                        setShowEditModal(false);
                        setSelectedOrigin(null);
                        setConfirmModal({
                            isOpen: true,
                            type: "success",
                            title: "Origen actualizado",
                            message: "Los datos del origen se guardaron correctamente.",
                            onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false }),
                        });
                        if (gridApiRef.current?.purgeInfiniteCache) {
                            setLoading(true);
                            gridApiRef.current.purgeInfiniteCache();
                        } else if (gridApiRef.current?.setDatasource) {
                            setLoading(true);
                            gridApiRef.current.setDatasource(createDataSource());
                        }
                    }}
                    onCancel={() => {
                        setShowEditModal(false);
                        setSelectedOrigin(null);
                    }}
                />
            </ReusableModal>

            {/* Confirm modal */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                type={confirmModal.type}
                title={confirmModal.title}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
            />
        </>
    );

};

export default OriginsList;
